'use client';

/**
 * NotConnectedState — the honest state for a page whose UI is built but whose
 * data service is not live yet (see `lib/api/notConnected.ts`).
 *
 *     if (isNotConnected(q.error)) return <NotConnectedState what="Departments" endpoints={['GET /org/departments']} />;
 *
 * It says what the page will hold and that nothing is shown because the
 * service is not connected — never sample data. `endpoints` lists what the
 * backend owes, so a reviewer sees the contract. `compact` for a panel.
 */
import { PlugZap } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn';

export interface NotConnectedStateProps {
  /** What the page or panel holds, e.g. "Departments". */
  what: string;
  /** One sentence on what will appear here once connected. */
  description?: ReactNode;
  /** Endpoints the backend owes, shown in a muted list. */
  endpoints?: string[];
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function NotConnectedState({
  what,
  description,
  endpoints,
  action,
  compact = false,
  className,
}: NotConnectedStateProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-dashed border-state-info-border bg-state-info-bg/40 text-sm',
        compact ? 'p-4' : 'p-6',
        className,
      )}
      role="status"
    >
      <div className="flex items-start gap-3">
        <PlugZap aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-state-info" />
        <div className="min-w-0 space-y-1">
          <p className="font-medium text-fg">{what} is not connected yet</p>
          <p className="text-fg-secondary">
            {description ??
              'This screen is ready, but the service that stores its data has not been switched on. Nothing is shown rather than sample data.'}
          </p>
          {endpoints && endpoints.length > 0 ? (
            <details className="pt-1">
              <summary className="cursor-pointer text-xs text-fg-muted">What the service needs</summary>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 font-mono text-xs text-fg-muted">
                {endpoints.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </details>
          ) : null}
          {action ? <div className="pt-2">{action}</div> : null}
        </div>
      </div>
    </div>
  );
}
